import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/use-auth';
import bdseLogoUrl from '../assets/bdse-logo.jpg';

/**
 * Login page. Visual parity with v0.7 (index.html LoginScreen, line ~5956).
 *
 * Auth flow: email + password via supabase.auth.signInWithPassword.
 * On success, AuthProvider's onAuthStateChange listener resolves to
 * 'ready' or 'unprovisioned'. AppRouter handles the redirect from there:
 *   - 'ready' state's router branch maps /login -> <Navigate to="/" replace />
 *   - 'unprovisioned' state routes everything to ProfileNotProvisioned
 * We deliberately do NOT navigate from this component to avoid side effects
 * during render and to keep routing logic centralized in AppRouter.
 *
 * No self-registration. Accounts are provisioned by admins via Supabase
 * Studio; the active user_profiles row gates platform access (provisioning
 * gate documented in arch doc 005).
 */
export function Login() {
  const { signInWithPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true);
    setError(null);
    const result = await signInWithPassword(email, password);
    if (!result.ok) {
      setError(result.message);
      setBusy(false);
      return;
    }
    // Success: AuthProvider's onAuthStateChange listener will update status.
    // AppRouter handles the redirect away from /login automatically.
    // We do NOT call navigate() here.
    setBusy(false);
  }

  function handleEnter(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="bdse-glass-card w-[420px] max-w-[92vw] px-10 pt-8 pb-7 text-center">
        {/*
         * Logo. v0.7 uses mix-blend-mode: multiply so the JPEG's white background
         * blends into the warm radial page background. -mb-2 -mt-1 matches the
         * tight negative-margin spacing from index.html line 432.
         */}
        <div className="-mt-1 -mb-2">
          <img
            src={bdseLogoUrl}
            alt="BD Signal Engine"
            className="mx-auto w-60 h-auto"
            style={{ mixBlendMode: 'multiply' }}
          />
        </div>

        <p className="bdse-tagline mb-4">Early Demand and Pursuit Intelligence</p>

        {error && (
          <div className="bdse-error-banner mb-3" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 text-left">
          <div>
            <label htmlFor="email" className="bdse-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="bdse-input"
              disabled={busy}
            />
          </div>

          <div>
            <label htmlFor="password" className="bdse-label">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleEnter}
              placeholder="Password"
              className="bdse-input"
              disabled={busy}
            />
          </div>

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="bdse-btn-primary w-full mt-1"
          >
            {busy ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-4 flex justify-center">
          <Link
            to="/forgot-password"
            className="text-xs text-bdse-text-tertiary hover:text-bdse-teal transition-colors duration-150"
          >
            Forgot password?
          </Link>
        </div>

        <div className="mt-4 pt-3.5 border-t border-black/[0.06]">
          <p className="bdse-footnote">Ankura BD Intelligence Platform</p>
        </div>
      </div>
    </div>
  );
}
